import {
  type ChatLookName,
  type Device,
  GameUiLayer,
  type MatchChat,
  type OverlayData,
  type PanelRequest,
  type PauseChatRow,
  type PlayerSettings,
  popupSeconds,
  useToastQueue,
} from '@mogzo/game-ui';
import {
  DEFAULT_GAME_UI,
  type GameEvent,
  type GameUiLook,
  type GameView,
  type LaunchParams,
} from '@mogzo/shared';
import { type Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePlayApi } from './api';
import { connectGameBridge, type GameBridge, sendGameEvent } from './game-bridge';
import { readGameSettings, saveGameSettings } from './game-settings';
import { PlayError } from './play';

/** After this long without loading, players are offered a restart. */
const LOAD_TIMEOUT_MS = 15_000;

export interface GameFrameHandle {
  /** Tells the game it's paused or back (the SDK's onPause and onResume). */
  send(name: GameEvent): void;
  /** Loads the page again from the start. */
  restart(): void;
  /** Opens one of MOGZO's panels over the game, such as the pause menu. */
  openPanel(panel: PanelRequest): void;
  /** Tells the game a player in its match sent a quick phrase (M38b). */
  phrase(name: string, phrase: string, mine: boolean): void;
}

/** MOGZO's panels over this frame (M19), and what the pause menu can do. */
export interface FramePanels {
  device: Device;
  gameName: string;
  /** Draw the panels over the whole page, not just the frame (a splash in a phone's feed). */
  overPage?: boolean;
  /** On a phone, leave room at the top left for MOGZO's pause button. */
  besidePauseButton?: boolean;
  onExit?: () => void;
}

/**
 * Chat in the game's match (M38b), from the page: the chat itself (null while the game isn't in
 * a match with chat), and what the game's `mogzo.chat` calls and MOGZO's pause menu open.
 */
export interface FrameChat {
  chat: MatchChat | null;
  /** The game started, opened or left a match (null). */
  onMatch: (matchId: string | null) => void;
  /** The player's own messages, for the pause menu. */
  messages?: PauseChatRow | null;
  /** Where the page shows the panel look itself (beside the game on a computer). */
  openPanel?: () => void;
}

/**
 * A game's splash or the game itself, in a locked-down frame from the game domain, with the
 * SDK bridge and MOGZO's panels over it. `allow-same-origin` lets engines like Unity and Godot
 * use storage; that is only safe because the game domain is a different site from mogzo.com
 * (TECH-STACK section 4).
 */
export function GameFrame({
  ref,
  url,
  title,
  projectId,
  view = 'game',
  launch,
  invite = null,
  openMatch = null,
  onLaunch,
  isDeveloper = false,
  onLoad,
  panels,
  chat,
}: {
  ref?: Ref<GameFrameHandle>;
  url: string;
  title: string;
  projectId: string;
  view?: GameView;
  /** For the game: what its splash launched it with. */
  launch?: LaunchParams;
  /** For the game: an invite code from a match link (M31), for the game to join with. */
  invite?: string | null;
  /** For the game: a match to open, from a notification or email (M33). */
  openMatch?: string | null;
  /** For a splash: its Play was pressed. */
  onLaunch?: (params: LaunchParams) => void;
  isDeveloper?: boolean;
  onLoad?: () => void;
  panels?: FramePanels;
  chat?: FrameChat;
}) {
  const api = usePlayApi();
  const frame = useRef<HTMLIFrameElement>(null);
  const [run, setRun] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [connected, setConnected] = useState(false);
  const [slow, setSlow] = useState(false);
  const gameOrigin = new URL(url).origin;
  const launchKey = JSON.stringify(launch ?? {});
  const onLaunchRef = useRef(onLaunch);
  onLaunchRef.current = onLaunch;

  const [panel, setPanel] = useState<PanelRequest | null>(null);
  const [look, setLook] = useState<GameUiLook>(DEFAULT_GAME_UI);
  const [data, setData] = useState<OverlayData | null>(null);
  const [settings, setSettings] = useState<PlayerSettings>(() => readGameSettings(projectId));
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const { toast, push } = useToastQueue(popupSeconds(look, panels?.device ?? 'computer', view));
  const pushRef = useRef(push);
  pushRef.current = push;
  /** The game's own ui.* calls waiting for their panel to close, and what it closed with. */
  const waiting = useRef<((result: unknown) => void)[]>([]);
  const closedWith = useRef<unknown>(undefined);
  const wasOpen = useRef(false);
  const withPanels = Boolean(panels);
  const bridgeRef = useRef<GameBridge | null>(null);
  const chatRef = useRef(chat);
  chatRef.current = chat;
  const [chatOpen, setChatOpen] = useState<ChatLookName | null>(null);
  /** How many of the match chat's messages the player has seen, for the pause menu's count. */
  const [chatSeen, setChatSeen] = useState(0);
  const chatMessages = chat?.chat?.messages ?? [];
  const chatUnread = chatMessages.slice(chatSeen).filter((m) => !m.mine).length;
  useEffect(() => {
    if (chatOpen) setChatSeen(chatMessages.length);
  }, [chatOpen, chatMessages.length]);
  useEffect(() => {
    if (!chat?.chat) setChatOpen(null);
  }, [chat?.chat]);
  const openChat = (look?: ChatLookName) => {
    const current = chatRef.current;
    const shown = look ?? current?.chat?.look ?? 'panel';
    if (shown === 'panel' && current?.openPanel) current.openPanel();
    else setChatOpen(shown);
  };
  const openChatRef = useRef(openChat);
  openChatRef.current = openChat;

  // biome-ignore lint/correctness/useExhaustiveDependencies: a restart reconnects the new frame
  useEffect(() => {
    const bridge = connectGameBridge({
      frame: () => frame.current,
      gameOrigin,
      projectId,
      api,
      view,
      launch: JSON.parse(launchKey) as LaunchParams,
      invite,
      openMatch,
      onLaunch: view === 'splash' ? (params) => onLaunchRef.current?.(params) : undefined,
      onConnected: () => setConnected(true),
      settings: () => ({ sound: settingsRef.current.sound }),
      onUnlock: (achievement) => pushRef.current(achievement),
      onMatch: (matchId) => chatRef.current?.onMatch(matchId),
      chat: {
        open: (look) => openChatRef.current(look),
        close: () => setChatOpen(null),
        say: async (phrase) => {
          const current = chatRef.current?.chat;
          if (!current) {
            throw new PlayError(409, 'no_match_chat', 'This match has no chat.');
          }
          const refused = await current.send(phrase, true);
          if (refused) throw new PlayError(422, 'not_sent', refused);
        },
      },
      closePanel: (result) =>
        setPanel((open) => {
          if (open?.kind !== 'room') return open;
          closedWith.current = result;
          return null;
        }),
      openPanel: withPanels
        ? (request) => {
            if (view === 'splash' && request.kind === 'pause') {
              return Promise.reject(
                new PlayError(400, 'not_available', 'A splash has no pause menu.'),
              );
            }
            return new Promise<unknown>((resolve) => {
              waiting.current.push(resolve);
              closedWith.current = undefined;
              setPanel(request);
            });
          }
        : undefined,
    });
    bridgeRef.current = bridge;
    setData(bridge.data);
    if (withPanels) bridge.look().then(setLook, () => undefined);
    return bridge.disconnect;
  }, [run, gameOrigin, projectId, api, view, launchKey, invite, openMatch, withPanels]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: each restart gets its own timer
  useEffect(() => {
    setLoaded(false);
    setConnected(false);
    setSlow(false);
    const timer = setTimeout(() => setSlow(true), LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [run]);

  // The game is paused while a panel is open, and its ui.* calls answer when it closes.
  useEffect(() => {
    const open = panel !== null;
    if (open === wasOpen.current) return;
    wasOpen.current = open;
    if (view === 'game') sendGameEvent(frame.current, gameOrigin, open ? 'pause' : 'resume');
    // Saves waiting to go are sent while the game is paused (M24b).
    if (open) void bridgeRef.current?.flush();
    if (!open) {
      const answers = waiting.current;
      waiting.current = [];
      for (const answer of answers) answer(closedWith.current);
    }
  }, [panel, view, gameOrigin]);

  useImperativeHandle(
    ref,
    () => ({
      send: (name) => {
        if (name === 'pause') void bridgeRef.current?.flush();
        sendGameEvent(frame.current, gameOrigin, name);
      },
      restart: () => setRun((n) => n + 1),
      // The multiplayer and room panels stay until the player is done with them (the page's Esc
      // can't replace them).
      openPanel: (request) =>
        setPanel((open) => (open?.kind === 'match' || open?.kind === 'room' ? open : request)),
      phrase: (name, phrase, mine) => bridgeRef.current?.phrase(name, phrase, mine),
    }),
    [gameOrigin],
  );

  function changeSettings(next: PlayerSettings) {
    setSettings(next);
    saveGameSettings(projectId, next);
    sendGameEvent(frame.current, gameOrigin, 'settings', { sound: next.sound });
  }

  const here = `${window.location.pathname}${window.location.search}`;
  const links = api.accountLinks?.(here) ?? {
    signIn: `/sign-in?next=${encodeURIComponent(here)}`,
    signUp: `/sign-up?next=${encodeURIComponent(here)}`,
  };
  const layer =
    panels && data ? (
      <GameUiLayer
        look={look}
        device={panels.device}
        screen={view}
        gameName={panels.gameName}
        panel={panel}
        toast={toast}
        data={data}
        settings={settings}
        onSettings={changeSettings}
        onOpen={setPanel}
        onClose={(result) => {
          closedWith.current = result;
          setPanel(null);
        }}
        onRestart={
          view === 'game'
            ? () => {
                setPanel(null);
                setRun((n) => n + 1);
              }
            : undefined
        }
        onExit={
          panels.onExit
            ? () => {
                setPanel(null);
                panels.onExit?.();
              }
            : undefined
        }
        signInUrl={links.signIn}
        signUpUrl={links.signUp}
        besidePauseButton={panels.besidePauseButton}
        chat={view === 'game' ? (chat?.chat ?? null) : null}
        chatOpen={chatOpen}
        onChatOpen={(look) => (look ? openChat(look) : setChatOpen(null))}
        chatUnread={chatUnread}
        messages={chat?.messages ?? null}
      />
    ) : null;

  return (
    <>
      <iframe
        key={run}
        ref={frame}
        src={url}
        title={title}
        sandbox="allow-scripts allow-same-origin allow-pointer-lock allow-forms"
        allow="autoplay; gamepad"
        referrerPolicy="no-referrer"
        onLoad={() => {
          setLoaded(true);
          onLoad?.();
        }}
        className="absolute inset-0 size-full border-0"
      />
      {view === 'game' && slow && !loaded && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-ink/95 p-6 text-center text-white">
          <p className="text-xl font-bold">The game is still loading</p>
          <p className="text-white/80">
            It hasn’t finished loading after 15 seconds. You can keep waiting, or restart it.
          </p>
          <button
            type="button"
            onClick={() => setRun((n) => n + 1)}
            className="inline-flex min-h-12 items-center rounded-full bg-brand px-6 font-bold text-ink"
          >
            Restart the game
          </button>
        </div>
      )}
      {view === 'game' && isDeveloper && slow && loaded && !connected && (
        <p className="absolute inset-x-3 bottom-3 rounded-xl bg-ink/90 px-3.5 py-2.5 text-[13px] leading-relaxed text-white/85">
          <strong className="text-white">Only you see this:</strong> the game didn’t call{' '}
          <span className="font-mono">mogzo.init()</span>. Games without the SDK still play, but get
          no player name, saves or posts.
        </p>
      )}
      {layer &&
        (panels?.overPage
          ? panel && createPortal(<div className="fixed inset-0 z-50">{layer}</div>, document.body)
          : layer)}
    </>
  );
}
